import React from 'react';
import { Pane } from '@ui/molecules/Pane';
import { Text } from '@ui';
import { translate } from '@utils/i18n';
import useGetIntegrationStatus from '@hooks/requests/integration-status.get.hook';
import { SyncStatusRow } from '../SyncStatusRow';
import { SyncErrorBanner } from '../SyncErrorBanner';
import { SyncHealthBadge } from '../SyncHealthBadge';
import { resolveRowStatus, findMostRecentError } from '../types';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import './SyncStatusPanelStyles.scss';
const NAMESPACE = 'features.settings.integrations.sync_status_panel';
/**
 * Shared orchestrator panel for integration sync status.
 * Fetches status data on mount and polls every 30 s.
 */
export function SyncStatusPanel({ integration, title, subtitle, rows, footerActivityCategory, footerNote, healthyLabel }) {
    var _a;
    const { data, loading, error } = useGetIntegrationStatus(integration);
    if (loading && !data) {
        return (React.createElement(Pane, { title: title, subtitle: subtitle },
            React.createElement("div", { className: "sync-status-panel__loading" },
                React.createElement("i", { className: "fa-light fa-circle-notch fa-spin", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body" }, translate('loading', { namespace: NAMESPACE })))));
    }
    const categoryMap = Object.fromEntries(((_a = data === null || data === void 0 ? void 0 : data.categories) !== null && _a !== void 0 ? _a : []).map((cat) => [cat.category, cat]));
    const rowCategories = rows.map((rowConfig) => categoryMap[rowConfig.category]).filter(Boolean);
    // A failed fetch with no data must not render as healthy/green.
    const hasFetchError = Boolean(error) && !data;
    const anyRed = hasFetchError || rowCategories.some((cat) => resolveRowStatus(cat) === 'red');
    const mostRecentError = findMostRecentError(rowCategories);
    const headerPill = buildHeaderPill(anyRed, healthyLabel);
    const activityCategory = footerActivityCategory ? categoryMap[footerActivityCategory] : undefined;
    const activityTime = (activityCategory === null || activityCategory === void 0 ? void 0 : activityCategory.lastSuccessAt)
        ? formatRelativeTime(activityCategory.lastSuccessAt, translate('today', { namespace: NAMESPACE }))
        : null;
    const footer = buildFooter(activityTime, footerNote, NAMESPACE);
    return (React.createElement(Pane, { title: title, subtitle: subtitle, headerRight: headerPill, footer: footer },
        anyRed && mostRecentError && React.createElement(SyncErrorBanner, { errorCategory: mostRecentError }),
        React.createElement("div", { className: "sync-status-panel__rows" }, rows.map((rowConfig) => {
            const cat = categoryMap[rowConfig.category];
            if (!cat)
                return null;
            return (React.createElement(SyncStatusRow, { key: rowConfig.category, label: rowConfig.label, category: cat, syncable: rowConfig.syncable }));
        }))));
}
function buildHeaderPill(anyRed, healthyLabel) {
    if (anyRed) {
        return React.createElement(SyncHealthBadge, { state: "attention", label: translate('attention_needed', { namespace: NAMESPACE }) });
    }
    return React.createElement(SyncHealthBadge, { state: "healthy", label: healthyLabel });
}
function buildFooter(activityTime, footerNote, ns) {
    return (React.createElement("div", { className: "sync-status-panel__footer" },
        activityTime && (React.createElement(Text, { variant: "x-small", className: "sync-status-panel__footer-activity" }, translate('last_activity', { namespace: ns, time: activityTime }))),
        React.createElement(Text, { variant: "x-small", className: "sync-status-panel__footer-note" }, footerNote)));
}
